import { useEffect, useRef } from "react";
import { useAppSelector } from "@/app/appStore";

import { useTimeline } from "@/shared/hooks/useTimeline";
import { useTimelineSizing } from "@/shared/hooks/useTimelineSizing";
import { useTimelineZoom } from "@/shared/hooks/useTimelineZoom";
import { useVisibleCursor } from "@/shared/hooks/useVisibleCursor";
import { useTimelineBackground } from "./useTimelineBackground";
import { useTimelineSchedules } from "./useTimelineSchedules";
import { useTimelineSeekHandlers } from "./useTimelineSeek";
import { timelineOptions } from "./timeline-options";

export function Timeline() {
  const timelineOuterContainerRef = useRef<HTMLDivElement | null>(null);
  const timebarRef = useRef<HTMLDivElement | null>(null);
  const TIME_MARKER_ID = "time-cursor";
  const LIVE_EDGE_MARKER_ID = "live-edge";
  const currentTime = useAppSelector((state) => state.player.currentTime);
  const liveEdge = useAppSelector((state) => state.player.liveEdge);

  const { isSeeking, ...handlers } = useTimelineSeekHandlers();
  const { timelineRef, containerRef, dataset } = useTimeline(handlers);

  useTimelineSizing({
    containerRef: timelineOuterContainerRef,
    timelineRef,
  });

  useEffect(() => {
    if (!timelineRef.current || isSeeking.current) return;

    if (currentTime) {
      try {
        timelineRef.current?.setCustomTime(currentTime, TIME_MARKER_ID);
      } catch {
        timelineRef.current?.addCustomTime(currentTime, TIME_MARKER_ID);
      }
    }
  }, [currentTime, timelineRef, isSeeking]);

  useEffect(() => {
    if (!timelineRef.current || isSeeking.current) return;

    if (!liveEdge) {
      try {
        timelineRef.current?.removeCustomTime(LIVE_EDGE_MARKER_ID);
      } catch (err) {
        console.warn(err);
      }
    } else {
      try {
        timelineRef.current?.setCustomTime(liveEdge, LIVE_EDGE_MARKER_ID);
      } catch {
        timelineRef.current?.addCustomTime(liveEdge, LIVE_EDGE_MARKER_ID);
      }
    }
  }, [liveEdge, timelineRef, isSeeking]);

  useVisibleCursor({
    timelineRef,
  });

  useTimelineZoom({
    timelineRef,
    getZoomMinMax: () => ({
      zoomMax: timelineOptions.zoomMax as number,
      zoomMin: timelineOptions.zoomMin as number,
    }),
  });

  useTimelineBackground({
    dataset,
    timelineRef,
  });

  useTimelineSchedules({
    dataset,
  });

  return (
    <div
      className="h-full timeline-outer-container planogram-timeline"
      ref={timelineOuterContainerRef}
    >
      <div id="timebar" ref={timebarRef} />
      <div ref={containerRef} className="h-full timeline-container"></div>
    </div>
  );
}
